// BidList.js
import React, { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { toast } from 'react-toastify';
import {
  DollarSign,
  Clock,
  TrendingUp,
  TrendingDown,
  Briefcase,
  AlertCircle,
  CheckCircle,
  XCircle,
  User,
  Calendar,
  MapPin,
  ArrowRight,
  Loader2
} from 'lucide-react';
import Modal from './Modal';
import { acceptBid, rejectBid, cancelBid } from '../utils/bidActions';
import { selectLoadById } from '../store/slices/loadSlice';
import { selectFeedLoadById, selectUserBidsForLoad } from '../store/slices/loadFeedSlice';
import { selectUserType, selectUserId, getCompanyName } from '../store/slices/authSlice';
import { createSelector } from '@reduxjs/toolkit';
import { formatDate, formatMilitaryTime } from '../utils/dateTimeUtils';
import useScreenSize from '../hooks/useScreenSize';
import { getLoadDisplayId } from '../utils/loadUtils';


// Memoized selector for grouped bids
const selectGroupedBids = createSelector(
  [selectLoadById, selectFeedLoadById, selectUserType, getCompanyName, selectUserId, selectUserBidsForLoad],
  (userLoad, feedLoad, userType, companyName, userId, userBidsForLoad) => {
    const isCarrier = userType === 'carrier' || userType === 'carrier_admin';
    
    // For carriers, we need to use the correct source of bids
    let bids = [];
    if (isCarrier) {
      // First try to get bids from userBidsForLoad selector (which may be more reliable)
      if (userBidsForLoad && userBidsForLoad.length > 0) {
        bids = userBidsForLoad;
      } else {
        // Fall back to feedLoad bids
        bids = feedLoad?.bids || [];
      }
    } else {
      // For customers, use userLoad bids
      bids = userLoad?.bids || [];
    }

    if (bids.length === 0) {
      return { yes: {}, alternative: {} };
    }

    if (isCarrier) {
      // Group carrier's own bids by type
      const yesBids = bids.filter(bid => !bid.bid_type || bid.bid_type === 'yes');
      const altBids = bids.filter(bid => bid.bid_type === 'alternative');
      
      return {
        yes: {
          [userId]: {
            carrier: { company_name: companyName },
            bids: yesBids,
          }
        },
        alternative: {
          [userId]: {
            carrier: { company_name: companyName },
            bids: altBids,
          }
        }
      };
    } else {
      // Group all bids by type and carrier
      const result = { yes: {}, alternative: {} };
      
      bids.forEach(bid => {
        // Ensure bid_type is always defined, default to 'yes'
        const bidType = bid.bid_type || bid.bidType || 'yes';
        const key = bid.carrier_id;
        
        if (!result[bidType][key]) {
          result[bidType][key] = {
            carrier: bid.carrier || { company_name: 'Unknown Carrier' },
            bids: [],
          };
        }
        
        result[bidType][key].bids.push(bid);
      });
      
      return result;
    }
  }
);

const BidList = React.memo(({ loadId, isOpen, onClose }) => {
  const userType = useSelector(selectUserType);
  const user = useSelector((state) => state.auth.user);
  const isCarrier = userType === 'carrier' || userType === 'carrier_admin';
  const isCustomer = userType === 'customer' || userType === 'customer_admin';
  const isViewOnly = user?.isViewOnly || false;
  const [activeTab, setActiveTab] = useState('yes');
  const [loadingBidId, setLoadingBidId] = useState(null);
  const { isMobile, isTablet } = useScreenSize();

  // Get the load object to retrieve the display ID (UUID)
  const userLoad = useSelector(state => selectLoadById(state, loadId));
  const feedLoad = useSelector(state => selectFeedLoadById(state, loadId));
  const load = userLoad || feedLoad;
  const displayId = getLoadDisplayId(load);

  const groupedBids = useSelector(state => selectGroupedBids(state, loadId));
  
  // Handle tab switching
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Filter carriers with no bids for the active tab
  const filteredBids = useMemo(() => {
    const result = {};
    
    Object.entries(groupedBids[activeTab] || {}).forEach(([key, data]) => {
      if (data.bids && data.bids.length > 0) {
        result[key] = data;
      }
    });
    
    return result;
  }, [groupedBids, activeTab]);

  // Count bids in each category
  const bidCounts = useMemo(() => {
    const countBids = (category) => {
      let total = 0;
      Object.values(groupedBids[category] || {}).forEach(data => {
        total += (data.bids?.length || 0);
      });
      return total;
    };
    return {
      yes: countBids('yes'),
      alternative: countBids('alternative')
    };
  }, [groupedBids]);

  const handleAcceptBid = async (bidId) => {
    setLoadingBidId(bidId);
    try {
      await acceptBid(bidId);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
      toast.success('Bid accepted successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error accepting bid:', error);
      toast.error('Failed to accept bid. Please try again.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoadingBidId(null);
    }
  };

  const handleRejectBid = async (bidId) => {
    setLoadingBidId(bidId);
    try {
      await rejectBid(bidId);
      toast.info('Bid rejected.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error rejecting bid:', error);
      toast.error('Failed to reject bid. Please try again.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoadingBidId(null);
    }
  };

  const handleCancelBid = async (bidId) => {
    setLoadingBidId(bidId);
    try {
      await cancelBid(bidId);
      toast.info('Bid cancelled successfully.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error cancelling bid:', error);
      toast.error('Failed to cancel bid. Please try again.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoadingBidId(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isCarrier ? `Your Bids for Load #${displayId}` : `Bids for Load #${displayId}`}
    >
      {/* Bid counts summary */}
      {(bidCounts.yes > 0 || bidCounts.alternative > 0) && (
        <div className="flex items-center justify-center gap-4 mb-3">
          <div className="flex items-center gap-1.5">
            <span className={`inline-flex items-center justify-center ${isMobile ? 'w-5 h-5 text-[10px]' : 'w-6 h-6 text-xs'} font-bold rounded-full ${
              bidCounts.yes > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'
            }`}>
              {bidCounts.yes}
            </span>
            <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium ${bidCounts.yes > 0 ? 'text-gray-600' : 'text-gray-400'}`}>
              {isCarrier ? 'Standard' : 'Yes'}
            </span>
          </div>
          <div className="w-px h-4 bg-gray-200" />
          <div className="flex items-center gap-1.5">
            <span className={`inline-flex items-center justify-center ${isMobile ? 'w-5 h-5 text-[10px]' : 'w-6 h-6 text-xs'} font-bold rounded-full ${
              bidCounts.alternative > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-400'
            }`}>
              {bidCounts.alternative}
            </span>
            <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium ${bidCounts.alternative > 0 ? 'text-gray-600' : 'text-gray-400'}`}>
              Alternative
            </span>
          </div>
        </div>
      )}

      {/* Mobile-optimized tabs for both customers and carriers */}
      <div className="mb-4 sm:mb-6 border-b border-gray-200">
        <div className={`flex ${isMobile ? 'space-x-1' : '-mb-px space-x-8'}`}>
          <button
            className={`${isMobile ? 'flex-1 py-3 px-2' : 'py-4 px-1'} border-b-2 font-medium ${
              isMobile ? 'text-xs' : 'text-sm'
            } transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'yes'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => handleTabChange('yes')}
          >
            {isCarrier ? (isMobile ? 'Standard' : 'Standard Bids') : (isMobile ? 'Yes' : 'Yes Bids')}
            {bidCounts.yes > 0 && (
              <span className={`inline-flex items-center justify-center ${isMobile ? 'w-4 h-4 text-[9px]' : 'w-5 h-5 text-[10px]'} font-semibold rounded-full ${
                activeTab === 'yes' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {bidCounts.yes}
              </span>
            )}
          </button>
          <button
            className={`${isMobile ? 'flex-1 py-3 px-2' : 'py-4 px-1'} border-b-2 font-medium ${
              isMobile ? 'text-xs' : 'text-sm'
            } transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'alternative'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => handleTabChange('alternative')}
          >
            {isMobile ? 'Alternative' : 'Alternative Bids'}
            {bidCounts.alternative > 0 && (
              <span className={`inline-flex items-center justify-center ${isMobile ? 'w-4 h-4 text-[9px]' : 'w-5 h-5 text-[10px]'} font-semibold rounded-full ${
                activeTab === 'alternative' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {bidCounts.alternative}
              </span>
            )}
          </button>
        </div>
      </div>

      {Object.keys(filteredBids || {}).length === 0 ? (
        <EmptyState isCarrier={isCarrier} bidType={activeTab} />
      ) : (
        <div className={`${isMobile ? 'space-y-4' : 'space-y-6'} max-h-[60vh] overflow-y-auto ${isMobile ? 'pr-1' : 'pr-2'}`}>
          {Object.entries(filteredBids || {}).map(([key, { carrier, bids }]) => (
            <CarrierBidGroup
              key={key}
              carrier={carrier}
              bids={bids}
              isCustomer={isCustomer}
              isMobile={isMobile}
              onAcceptBid={handleAcceptBid}
              onRejectBid={handleRejectBid}
              onCancelBid={handleCancelBid}
              loadId={loadId}
              bidType={activeTab}
              isViewOnly={isViewOnly}
              loadingBidId={loadingBidId}
            />
          ))}
        </div>
      )}
    </Modal>
  );
});

const EmptyState = ({ isCarrier, bidType }) => {
  const { isMobile } = useScreenSize();
  let message = '';
  
  if (isCarrier) {
    message = bidType === 'alternative' 
      ? "You have not placed any alternative bids on this load yet."
      : "You have not placed any standard bids on this load yet.";
  } else {
    message = bidType === 'alternative' 
      ? "No alternative bids have been placed for this load yet."
      : "No yes bids have been placed for this load yet.";
  }
  
  return (
    <div className={`flex items-center justify-center ${isMobile ? 'py-8' : 'py-12'}`}>
      <div className="text-center px-4">
        <AlertCircle size={isMobile ? 40 : 48} className="text-gray-400 mx-auto mb-4" />
        <p className={`${isMobile ? 'text-lg' : 'text-xl'} text-gray-600 leading-relaxed`}>{message}</p>
      </div>
    </div>
  );
};

const CarrierBidGroup = ({
  carrier,
  bids,
  isCustomer,
  isMobile,
  onAcceptBid,
  onRejectBid,
  onCancelBid,
  loadId,
  bidType,
  isViewOnly,
  loadingBidId,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className={`bg-white rounded-xl ${isMobile ? 'p-4' : 'p-6'} shadow-lg border border-gray-200`}
  >
    <div className={`flex items-center ${isMobile ? 'flex-col space-y-2' : 'justify-between'} mb-4`}>
      <div className="flex items-center">
        <Briefcase size={isMobile ? 20 : 24} className="text-blue-500 mr-3" />
        <span className={`font-bold ${isMobile ? 'text-lg' : 'text-xl'} text-gray-800`}>
          {carrier.company_name}
        </span>
      </div>
      {isCustomer && (
        <div className="flex items-center space-x-2">
          <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-500`}>
            Total Bids:
          </span>
          <span className={`${isMobile ? 'text-base' : 'text-lg'} font-bold text-blue-600`}>
            {bids.length}
          </span>
        </div>
      )}
    </div>
    <ul className={`${isMobile ? 'space-y-3' : 'space-y-4'}`}>
      {bids.map((bid) => (
        <BidItem
          key={bid.id}
          bid={bid}
          isCustomer={isCustomer}
          isMobile={isMobile}
          onAcceptBid={onAcceptBid}
          onRejectBid={onRejectBid}
          onCancelBid={onCancelBid}
          isViewOnly={isViewOnly}
          loadingBidId={loadingBidId}
        />
      ))}
    </ul>
  </motion.div>
);

const BidItem = ({ bid, isCustomer, isMobile, onAcceptBid, onRejectBid, onCancelBid, isViewOnly, loadingBidId }) => {
  const isLoading = loadingBidId === bid.id;
  const isAnyLoading = loadingBidId !== null;
  
  return (
  <li className={`bg-gray-50 rounded-lg ${isMobile ? 'p-3' : 'p-4'} transition-all hover:shadow-md`}>
    <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'justify-between items-center'} mb-2`}>
      {!isViewOnly && (
        <div className="flex items-center">
          <DollarSign size={isMobile ? 18 : 20} className="text-green-500 mr-2" />
          <span className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-gray-900`}>
            ${bid.amount.toFixed(2)}
          </span>
          {bid.bid_type === 'alternative' && (
            <span className={`ml-2 px-2 py-1 bg-blue-100 text-blue-800 ${isMobile ? 'text-xs' : 'text-xs'} font-medium rounded-full`}>
              {isMobile ? 'Alt Schedule' : 'Alternative Schedule'}
            </span>
          )}
        </div>
      )}
      {isViewOnly && bid.bid_type === 'alternative' && (
        <div className="flex items-center">
          <span className={`px-2 py-1 bg-blue-100 text-blue-800 ${isMobile ? 'text-xs' : 'text-xs'} font-medium rounded-full`}>
            {isMobile ? 'Alt Schedule' : 'Alternative Schedule'}
          </span>
        </div>
      )}
      <BidStatus status={bid.status} isMobile={isMobile} />
    </div>
    
    {/* Show alternative dates if this is an alternative bid */}
    {bid.bid_type === 'alternative' && (
      <div className={`mt-2 mb-3 ${isMobile ? 'p-2' : 'p-3'} bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-100 shadow-sm`}>
        <div className="flex items-center gap-2 mb-3">
          <div className={`${isMobile ? 'p-1' : 'p-1.5'} bg-indigo-100 rounded-lg`}>
            <Calendar size={isMobile ? 12 : 14} className="text-indigo-600" />
          </div>
          <h4 className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-gray-800`}>
            Proposed Alternative Schedule
          </h4>
        </div>
        
        {/* Show all stops if alt_stops is available, otherwise fall back to legacy display */}
        {bid.alt_stops && bid.alt_stops.length > 0 ? (
          <div className={`space-y-0 ${isMobile ? 'max-h-48' : 'max-h-56'} overflow-y-auto pr-1`}>
            {bid.alt_stops.map((stopData, index, arr) => {
              const isLast = index === arr.length - 1;
              // Check for stop_type field - handle both snake_case and camelCase, with fallback
              const stopType = stopData.stop_type || stopData.stopType;
              const isPickup = stopType === 'pickup';
              // If stop_type is missing (legacy data), show as "Stop N" instead
              const hasStopType = !!stopType;
              const locationText = stopData.city && stopData.state 
                ? `${stopData.city}, ${stopData.state}` 
                : null;
              
              return (
                <div key={stopData.stop_id || index} className="relative">
                  {/* Timeline connector */}
                  <div className="absolute left-2.5 top-6 bottom-0 flex flex-col items-center">
                    {!isLast && (
                      <div className={`w-0.5 flex-grow ${
                        hasStopType 
                          ? (isPickup ? 'bg-blue-200' : 'bg-emerald-200')
                          : 'bg-indigo-200'
                      }`} />
                    )}
                  </div>
                  
                  <div className={`relative pl-8 ${isLast ? 'pb-0' : 'pb-2'}`}>
                    {/* Timeline dot */}
                    <div className={`absolute left-0 top-1 w-5 h-5 rounded-full flex items-center justify-center shadow-sm ${
                      hasStopType
                        ? (isPickup 
                            ? 'bg-gradient-to-br from-blue-400 to-blue-600' 
                            : 'bg-gradient-to-br from-emerald-400 to-emerald-600')
                        : 'bg-gradient-to-br from-indigo-400 to-indigo-600'
                    }`}>
                      <MapPin className="w-2.5 h-2.5 text-white" />
                    </div>
                    
                    {/* Stop card */}
                    <div className={`bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden`}>
                      {/* Header */}
                      <div className={`flex items-center justify-between ${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} ${
                        hasStopType 
                          ? (isPickup ? 'bg-blue-50/50' : 'bg-emerald-50/50')
                          : 'bg-gray-50/50'
                      }`}>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center ${isMobile ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'} rounded-md font-semibold ${
                            hasStopType
                              ? (isPickup ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700')
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {hasStopType ? (isPickup ? 'Pickup' : 'Delivery') : `Stop ${index + 1}`}
                          </span>
                          {locationText && (
                            <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium text-gray-600`}>
                              {locationText}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Schedule */}
                      <div className={`flex items-center gap-1.5 ${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} border-t border-gray-50`}>
                        <Clock size={isMobile ? 11 : 13} className="text-indigo-500" />
                        <span className={`${isMobile ? 'text-[11px]' : 'text-sm'} font-medium text-gray-700`}>
                          {formatDate(stopData.date)}
                          {stopData.time && (
                            <span className="text-gray-500 font-normal">
                              {' '}at {formatMilitaryTime(stopData.time)}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Legacy display for old bids without alt_stops */
          <div className={`${isMobile ? 'space-y-2' : 'space-y-0'}`}>
            {/* Pickup */}
            <div className="relative">
              <div className="absolute left-2.5 top-5 bottom-0 flex flex-col items-center">
                <div className="w-0.5 flex-grow bg-gradient-to-b from-blue-200 to-emerald-200" />
              </div>
              <div className="relative pl-7 pb-2">
                <div className="absolute left-0 top-0.5 w-5 h-5 rounded-full flex items-center justify-center shadow-sm bg-gradient-to-br from-blue-400 to-blue-600">
                  <MapPin className="w-2.5 h-2.5 text-white" />
                </div>
                <div className={`flex items-center justify-between bg-white rounded-lg ${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} border border-gray-100 shadow-sm`}>
                  <span className={`inline-flex items-center ${isMobile ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'} rounded-md font-semibold bg-blue-100 text-blue-700`}>
                    Pickup
                  </span>
                  <div className={`flex items-center gap-1 ${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-600`}>
                    <Clock size={isMobile ? 10 : 12} className="text-indigo-400" />
                    <span className="font-medium">
                      {formatDate(bid.alt_pickup_date || bid.altPickupDate)}
                      {(bid.alt_pickup_time || bid.altPickupTime) && 
                        ` at ${formatMilitaryTime(bid.alt_pickup_time || bid.altPickupTime)}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Delivery */}
            <div className="relative">
              <div className="relative pl-7">
                <div className="absolute left-0 top-0.5 w-5 h-5 rounded-full flex items-center justify-center shadow-sm bg-gradient-to-br from-emerald-400 to-emerald-600">
                  <MapPin className="w-2.5 h-2.5 text-white" />
                </div>
                <div className={`flex items-center justify-between bg-white rounded-lg ${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'} border border-gray-100 shadow-sm`}>
                  <span className={`inline-flex items-center ${isMobile ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'} rounded-md font-semibold bg-emerald-100 text-emerald-700`}>
                    Delivery
                  </span>
                  <div className={`flex items-center gap-1 ${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-600`}>
                    <Clock size={isMobile ? 10 : 12} className="text-indigo-400" />
                    <span className="font-medium">
                      {formatDate(bid.alt_delivery_date || bid.altDeliveryDate)}
                      {(bid.alt_delivery_time || bid.altDeliveryTime) && 
                        ` at ${formatMilitaryTime(bid.alt_delivery_time || bid.altDeliveryTime)}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )}
    
    <div className={`flex flex-col ${isMobile ? 'space-y-1.5' : 'space-y-2'}`}>
      <div className={`flex items-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>
        <Clock size={isMobile ? 14 : 16} className="mr-2" />
        <span>{new Date(bid.created_at).toLocaleString()}</span>
      </div>
      {!isCustomer && (
        <div className={`flex items-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>
          <User size={isMobile ? 14 : 16} className="mr-2" />
          <span>Placed by: {bid.bidder_name || 'You'}</span>
        </div>
      )}
      {bid.notes && (
        <div className={`flex items-start mt-2 bg-white rounded-lg ${isMobile ? 'p-2' : 'p-3'} border border-gray-200`}>
          <svg
            className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-gray-500 mt-1 mr-2 flex-shrink-0`}
            fill="none"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M7 8h10M7 12h10M7 16h10" />
          </svg>
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-700`}>{bid.notes}</p>
        </div>
      )}
    </div>

    {!isCustomer && bid.status === 'Pending' && (
      <div className={`${isMobile ? 'mt-3' : 'mt-4'} flex justify-end`}>
        <motion.button
          whileHover={!isAnyLoading ? { scale: 1.05 } : {}}
          whileTap={!isAnyLoading ? { scale: 0.95 } : {}}
          onClick={() => onCancelBid(bid.id)}
          disabled={isAnyLoading}
          className={`flex items-center ${isMobile ? 'px-3 py-2.5 text-sm' : 'px-4 py-2'} bg-gray-500 text-white rounded-lg transition-colors ${isMobile ? 'min-h-[44px]' : ''} ${
            isAnyLoading 
              ? 'opacity-50 cursor-not-allowed' 
              : 'hover:bg-gray-600'
          }`}
        >
          {isLoading ? (
            <Loader2 size={isMobile ? 16 : 18} className="mr-2 animate-spin" />
          ) : (
            <XCircle size={isMobile ? 16 : 18} className="mr-2" />
          )}
          {isLoading ? 'Cancelling...' : 'Cancel Bid'}
        </motion.button>
      </div>
    )}

    {isCustomer && bid.status === 'Pending' && !isViewOnly && (
      <BidActions bid={bid} isMobile={isMobile} onAcceptBid={onAcceptBid} onRejectBid={onRejectBid} isLoading={isLoading} isAnyLoading={isAnyLoading} />
    )}
  </li>
  );
};

const BidStatus = ({ status, isMobile }) => {
  const statusConfig = {
    Pending: { bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', icon: Clock },
    Accepted: { bgColor: 'bg-green-100', textColor: 'text-green-800', icon: CheckCircle },
    Rejected: { bgColor: 'bg-red-100', textColor: 'text-red-800', icon: XCircle },
    Cancelled: { bgColor: 'bg-gray-100', textColor: 'text-gray-800', icon: XCircle }
  };

  const config = statusConfig[status] || statusConfig.Pending;
  const { bgColor, textColor, icon: Icon } = config;

  return (
    <span
      className={`flex items-center ${isMobile ? 'text-xs' : 'text-sm'} font-medium ${
        isMobile ? 'px-2 py-1' : 'px-3 py-1'
      } rounded-full ${bgColor} ${textColor}`}
    >
      <Icon size={isMobile ? 14 : 16} className="mr-1" />
      {status}
    </span>
  );
};

const BidActions = ({ bid, isMobile, onAcceptBid, onRejectBid, isLoading, isAnyLoading }) => {
  return (
    <div className={`${isMobile ? 'mt-3' : 'mt-4'} flex ${isMobile ? 'flex-col space-y-2' : 'justify-end space-x-3'}`}>
      <motion.button
        whileHover={!isAnyLoading ? { scale: 1.05 } : {}}
        whileTap={!isAnyLoading ? { scale: 0.95 } : {}}
        onClick={() => onAcceptBid(bid.id)}
        disabled={isAnyLoading}
        className={`flex items-center justify-center ${
          isMobile ? 'px-3 py-2.5 text-sm min-h-[44px]' : 'px-4 py-2'
        } bg-green-500 text-white rounded-lg transition-colors ${
          isAnyLoading 
            ? 'opacity-50 cursor-not-allowed' 
            : 'hover:bg-green-600'
        }`}
      >
        {isLoading ? (
          <Loader2 size={isMobile ? 16 : 18} className="mr-2 animate-spin" />
        ) : (
          <TrendingUp size={isMobile ? 16 : 18} className="mr-2" />
        )}
        {isLoading ? 'Processing...' : 'Accept Bid'}
      </motion.button>
      <motion.button
        whileHover={!isAnyLoading ? { scale: 1.05 } : {}}
        whileTap={!isAnyLoading ? { scale: 0.95 } : {}}
        onClick={() => onRejectBid(bid.id)}
        disabled={isAnyLoading}
        className={`flex items-center justify-center ${
          isMobile ? 'px-3 py-2.5 text-sm min-h-[44px]' : 'px-4 py-2'
        } bg-red-500 text-white rounded-lg transition-colors ${
          isAnyLoading 
            ? 'opacity-50 cursor-not-allowed' 
            : 'hover:bg-red-600'
        }`}
      >
        {isLoading ? (
          <Loader2 size={isMobile ? 16 : 18} className="mr-2 animate-spin" />
        ) : (
          <TrendingDown size={isMobile ? 16 : 18} className="mr-2" />
        )}
        Reject Bid
      </motion.button>
    </div>
  );
};

export default BidList;
